/**
 * modes/drama-mode.js
 * Quản lý chế độ Hoạt Cảnh Tranh Khách (Street Drama 62s Cinematic) và bảng điều khiển đạo diễn.
 */

import { StreetDramaDirector, STREET_DRAMA_CUES } from '../../city/street-drama-director.js';
import { setupVietnamCityMode } from './city-mode.js';
import { escapeHtml } from '../../core/html.js';

export function setupDramaMode(state, helpers = {}) {
  const {
    clearAvatars,
    clearCityScene,
    clearLabBench,
    closeMobileDrawer,
    updateMobileFabVisibility,
    startVideoRecording
  } = helpers;

  if (typeof clearAvatars === 'function') clearAvatars(state);
  if (typeof clearCityScene === 'function') clearCityScene(state);
  if (typeof clearLabBench === 'function') clearLabBench(state);

  if (state.studioEnvironmentGroup) state.studioEnvironmentGroup.visible = false;
  if (state.pedestalGroup) state.pedestalGroup.visible = false;
  if (state.gridHelper) state.gridHelper.visible = false;

  const panel = document.querySelector('.control-panel');
  if (panel) panel.style.display = 'none';
  const hints = document.querySelector('.viewport-hints');
  if (hints) hints.style.display = 'none';
  const reopenBtn = document.getElementById('btn-reopen-hints');
  if (reopenBtn) reopenBtn.style.display = 'none';
  if (typeof closeMobileDrawer === 'function') closeMobileDrawer();
  if (typeof updateMobileFabVisibility === 'function') updateMobileFabVisibility();

  // Dựng nền phố phường đô thị Việt Nam (isDrama = true)
  setupVietnamCityMode(true, state, clearAvatars);

  if (!state.dramaDirector) {
    state.dramaDirector = new StreetDramaDirector(state.scene, state.camera, state.controls);
    setupDramaUI(state, { startVideoRecording });
  }

  state.scene.add(state.dramaDirector.group);

  const subtitleBox = document.getElementById('drama-subtitle-box');
  if (subtitleBox) subtitleBox.style.display = state.isDramaSubtitleEnabled ? 'block' : 'none';
  const playbackDock = document.getElementById('drama-playback-dock');
  if (playbackDock) playbackDock.style.display = 'flex';

  state.dramaDirector.restart();
}

export function setupDramaUI(state, helpers = {}) {
  const { startVideoRecording } = helpers;
  const dramaDirector = state.dramaDirector;
  if (!dramaDirector) return;

  const playBtn = document.getElementById('drama-btn-play');
  const playIcon = document.getElementById('drama-play-icon');
  const playText = document.getElementById('drama-play-text');
  const restartBtn = document.getElementById('drama-btn-restart');
  const slider = document.getElementById('drama-time-slider');
  const currentTimeEl = document.getElementById('drama-current-time');
  const speakerEl = document.getElementById('subtitle-speaker');
  const textEl = document.getElementById('subtitle-text');
  const camToggle = document.getElementById('drama-toggle-cam');
  const camText = document.getElementById('drama-cam-text');
  const soundToggle = document.getElementById('drama-toggle-sound');
  const ttsToggle = document.getElementById('drama-toggle-tts');
  const subToggle = document.getElementById('drama-toggle-sub');
  const subText = document.getElementById('drama-sub-text');
  const subtitleBox = document.getElementById('drama-subtitle-box');
  const sceneBtns = document.querySelectorAll('.drama-scene-btn');

  function updatePlayState(isPlaying) {
    if (playIcon) playIcon.textContent = isPlaying ? '⏸' : '▶';
    if (playText) playText.textContent = isPlaying ? 'Tạm dừng' : 'Phát';
    if (playBtn) playBtn.classList.toggle('primary', !isPlaying);
  }

  if (playBtn) {
    playBtn.addEventListener('click', () => {
      if (dramaDirector.isPlaying) {
        dramaDirector.pause();
        updatePlayState(false);
      } else {
        dramaDirector.play();
        updatePlayState(true);
      }
    });
  }

  if (restartBtn) {
    restartBtn.addEventListener('click', () => {
      dramaDirector.restart();
      updatePlayState(true);
    });
  }

  const recordBtn = document.getElementById('drama-btn-record');
  if (recordBtn) {
    recordBtn.addEventListener('click', () => {
      if (typeof startVideoRecording === 'function') startVideoRecording(state);
    });
  }

  if (slider) {
    slider.addEventListener('input', (e) => {
      const val = parseFloat(e.target.value);
      dramaDirector.seek(val);
    });
  }

  sceneBtns.forEach((btn) => {
    btn.addEventListener('click', () => {
      const scIndex = parseInt(btn.dataset.scene, 10);
      dramaDirector.jumpToScene(scIndex);
      dramaDirector.play();
      updatePlayState(true);
    });
  });

  if (camToggle) {
    camToggle.addEventListener('click', () => {
      if (dramaDirector.cameraMode === 'cinematic') {
        dramaDirector.cameraMode = 'free';
        camToggle.classList.remove('active');
        if (camText) camText.textContent = 'Tự Do 360°';
      } else {
        dramaDirector.cameraMode = 'cinematic';
        camToggle.classList.add('active');
        if (camText) camText.textContent = 'Cinematic Cam';
      }
    });
  }

  if (soundToggle) {
    soundToggle.addEventListener('click', () => {
      dramaDirector.isSoundEnabled = !dramaDirector.isSoundEnabled;
      soundToggle.classList.toggle('active', dramaDirector.isSoundEnabled);
      soundToggle.style.opacity = dramaDirector.isSoundEnabled ? '1' : '0.5';
    });
  }

  if (ttsToggle) {
    ttsToggle.classList.toggle('active', dramaDirector.isTtsEnabled);
    ttsToggle.style.opacity = dramaDirector.isTtsEnabled ? '1' : '0.6';
    ttsToggle.addEventListener('click', () => {
      dramaDirector.isTtsEnabled = !dramaDirector.isTtsEnabled;
      if (dramaDirector.audioManager) {
        dramaDirector.audioManager.setEnabled(dramaDirector.isTtsEnabled);
      }
      ttsToggle.classList.toggle('active', dramaDirector.isTtsEnabled);
      ttsToggle.style.opacity = dramaDirector.isTtsEnabled ? '1' : '0.6';
    });
  }

  if (subToggle) {
    subToggle.classList.toggle('active', state.isDramaSubtitleEnabled);
    if (subText) subText.textContent = state.isDramaSubtitleEnabled ? 'Phụ đề: Bật' : 'Phụ đề: Tắt';
    subToggle.addEventListener('click', () => {
      state.isDramaSubtitleEnabled = !state.isDramaSubtitleEnabled;
      subToggle.classList.toggle('active', state.isDramaSubtitleEnabled);
      if (subText) subText.textContent = state.isDramaSubtitleEnabled ? 'Phụ đề: Bật' : 'Phụ đề: Tắt';
      if (subtitleBox) subtitleBox.style.display = state.isDramaSubtitleEnabled ? 'block' : 'none';
    });
  }

  // Xử lý Modal Quản lý Âm thanh Edge-TTS / MP3 Override
  const voiceModalBtn = document.getElementById('drama-btn-voice-modal');
  const audioModal = document.getElementById('drama-audio-modal');
  const audioBackdrop = document.getElementById('drama-audio-backdrop');
  const audioModalClose = document.getElementById('drama-audio-modal-close');
  const cueListContainer = document.getElementById('drama-cue-list-container');
  const subtitleAudioBadge = document.getElementById('subtitle-audio-badge');

  function openAudioModal() {
    if (!audioModal || !audioBackdrop) return;
    renderAudioCueList();
    audioModal.style.display = 'flex';
    audioBackdrop.style.display = 'block';
  }

  function closeAudioModal() {
    if (!audioModal || !audioBackdrop) return;
    audioModal.style.display = 'none';
    audioBackdrop.style.display = 'none';
  }

  if (voiceModalBtn) voiceModalBtn.addEventListener('click', openAudioModal);
  if (audioModalClose) audioModalClose.addEventListener('click', closeAudioModal);
  if (audioBackdrop) audioBackdrop.addEventListener('click', closeAudioModal);

  function renderAudioCueList() {
    if (!cueListContainer || !dramaDirector || !dramaDirector.audioManager) return;
    cueListContainer.innerHTML = '';

    STREET_DRAMA_CUES.forEach((cue) => {
      const hasOverride = dramaDirector.audioManager.hasOverride(cue.id);
      const overrideInfo = dramaDirector.audioManager.getOverride(cue.id);
      const voiceCfg = dramaDirector.audioManager.getCharacterVoice(cue.characterId);
      const voiceShort = voiceCfg.voice.includes('-') ? voiceCfg.voice.split('-')[2] : voiceCfg.voice;

      const card = document.createElement('div');
      card.className = `drama-cue-card ${hasOverride ? 'has-override' : ''}`;
      card.dataset.cueId = cue.id;

      card.innerHTML = `
        <div class="drama-cue-info">
          <div class="drama-cue-meta">
            <span class="drama-cue-speaker">${escapeHtml(cue.speaker)}</span>
            <span class="drama-cue-scene">(Hồi ${cue.scene + 1})</span>
            <span class="drama-cue-badge ${hasOverride ? 'badge-mp3-override' : 'badge-edge-tts'}">
              ${hasOverride ? `🎵 MP3 Ghi Đè: ${escapeHtml(overrideInfo?.name || 'custom.mp3')}` : `⚡ Edge-TTS (${escapeHtml(voiceShort)})`}
            </span>
          </div>
          <div class="drama-cue-text">"${escapeHtml(cue.text)}"</div>
        </div>
        <div class="drama-cue-actions">
          <button class="cue-btn btn-test-play" title="Nghe thử âm thanh câu này">
            <span>▶</span> Thử
          </button>
          <label class="cue-btn btn-upload" title="Tải file MP3 để ghi đè câu này">
            <span>📂</span> MP3
            <input type="file" accept="audio/mp3,audio/*" class="cue-file-input u-hidden" />
          </label>
          ${
            hasOverride
              ? `
            <button class="cue-btn btn-revert" title="Xóa file MP3, hoàn tác về Edge-TTS">
              <span>↺</span> Bỏ ghi đè
            </button>
          `
              : ''
          }
        </div>
      `;

      const btnTest = card.querySelector('.btn-test-play');
      if (btnTest) {
        btnTest.addEventListener('click', () => {
          dramaDirector.audioManager.playCue({
            cueId: cue.id,
            text: cue.text,
            speaker: cue.speaker,
            characterId: cue.characterId
          });
        });
      }

      const fileInput = card.querySelector('.cue-file-input');
      if (fileInput) {
        fileInput.addEventListener('change', (e) => {
          const file = e.target.files && e.target.files[0];
          if (file) {
            dramaDirector.audioManager.registerOverride(cue.id, file, file.name);
            renderAudioCueList();
          }
        });
      }

      const btnRevert = card.querySelector('.btn-revert');
      if (btnRevert) {
        btnRevert.addEventListener('click', () => {
          dramaDirector.audioManager.removeOverride(cue.id);
          renderAudioCueList();
        });
      }

      cueListContainer.appendChild(card);
    });
  }

  // Lắng nghe tiến trình diễn hoạt từ Director
  dramaDirector.onProgress((info) => {
    const totalSec = Math.floor(info.currentTime);
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    const curSub = Math.floor((info.currentTime % 1) * 10);
    if (currentTimeEl) {
      currentTimeEl.textContent = `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}.${curSub}`;
    }
    if (slider) {
      slider.value = info.currentTime;
    }

    if (speakerEl && info.speaker) {
      speakerEl.textContent = info.speaker;
      if (info.speaker.includes('Grab')) {
        speakerEl.style.color = '#4ade80';
        speakerEl.style.background = 'rgba(34, 197, 94, 0.2)';
        speakerEl.style.borderColor = 'rgba(34, 197, 94, 0.5)';
      } else if (info.speaker.includes('Xanh')) {
        speakerEl.style.color = '#38bdf8';
        speakerEl.style.background = 'rgba(6, 182, 212, 0.2)';
        speakerEl.style.borderColor = 'rgba(6, 182, 212, 0.5)';
      } else if (info.speaker.includes('Ba')) {
        speakerEl.style.color = '#fde047';
        speakerEl.style.background = 'rgba(234, 179, 8, 0.2)';
        speakerEl.style.borderColor = 'rgba(234, 179, 8, 0.5)';
      } else {
        speakerEl.style.color = '#f8fafc';
        speakerEl.style.background = 'rgba(148, 163, 184, 0.2)';
        speakerEl.style.borderColor = 'rgba(148, 163, 184, 0.5)';
      }
    }
    if (textEl && info.subtitle) {
      textEl.textContent = info.subtitle;
    }

    if (subtitleAudioBadge && info.audioSource) {
      const isOverride = info.audioSource === 'mp3_override';
      subtitleAudioBadge.textContent = isOverride ? '🎵 MP3 Override' : '⚡ Edge-TTS On-The-Fly';
      subtitleAudioBadge.className = `drama-cue-badge ${isOverride ? 'badge-mp3-override' : 'badge-edge-tts'}`;
    }

    sceneBtns.forEach((btn) => {
      const sc = parseInt(btn.dataset.scene, 10);
      btn.classList.toggle('active', sc === info.sceneIndex);
    });

    updatePlayState(info.isPlaying);
  });
}
